/**
 * /platform/ai-assistant — SEO landing page for the AI Assistant tool.
 */
import React from 'react';
import {
  Bot, MessageSquare, Zap, BookOpen, Sparkles, Shield, Clock, Search,
  Calculator, Users, Video, Beaker, UserCheck, Thermometer, Timer,
} from 'lucide-react';
import PlatformFeaturePage from './PlatformFeaturePage';

const AIAssistantPage = () => (
  <PlatformFeaturePage
    slug="ai-assistant"
    eyebrow="AI Assistant"
    title="A freeze-dry assistant that"
    titleAccent="actually knows freeze-drying."
    subtitle="Ask any question about cycles, temperatures, moisture, machines or business ops — and get grounded answers cited to the academy knowledge base."
    seo={{
      title: 'AI Freeze Dry Assistant — Ask Anything, Get Cited Answers',
      description: 'Chatbot trained specifically on freeze-drying: cycle troubleshooting, batch math, machine ops, moisture targets. Free for members.',
      keywords: 'freeze dry ai, freeze drying chatbot, freeze dryer assistant, freeze drying troubleshooting ai',
    }}
    highlights={[
      { icon: Zap,       label: 'Instant answers' },
      { icon: BookOpen,  label: 'Cites source lessons' },
      { icon: Shield,    label: 'No hallucination guardrails' },
      { icon: Clock,     label: '24 / 7' },
    ]}
    primaryCta={{ label: 'Try the assistant free', to: '/login?mode=signup' }}
    secondaryCta={{ label: 'See the courses it draws from', to: '/platform/learning' }}
    howItWorks={[
      { title: 'Ask in plain English', body: 'Type "my strawberries came out chewy — what temp should I be running?" and hit enter. No prompt engineering required.' },
      { title: 'Grounded reasoning', body: 'The assistant retrieves relevant lessons, cycle data, and calculator formulas — then answers using them, with the source pinned to the reply.' },
      { title: 'Take action', body: 'Follow the citation to read the full lesson, or one-click into the calculator with the assistant\'s numbers pre-filled.' },
    ]}
    features={[
      { icon: MessageSquare, title: 'Multi-turn memory', body: 'Ask follow-ups and it remembers the machine you told it about, the product you\'re running, and the batch numbers you shared earlier.' },
      { icon: BookOpen,      title: 'Cited every answer',  body: 'No made-up "as an AI language model" — every claim links back to a specific lesson, calculator or forum thread.' },
      { icon: Thermometer,   title: 'Cycle troubleshooting',body: 'Describe the symptom (foamy, chewy, crystallized, undried center) and it walks you through the likely cause + how to fix it next batch.' },
      { icon: Timer,         title: 'Time-saving playbooks',body: 'Ask for a "starter cycle for chicken thighs on a mid-size home freeze dryer" and get a full temperature, pressure and time recipe tuned to your machine size — no brand names required, the assistant supports every home and commercial size on the market.' },
      { icon: Search,        title: 'Search across the KB',body: 'Even when you ask a question we haven\'t covered, it searches every course, every forum reply, and every calculator note in one shot.' },
      { icon: Shield,        title: 'Safe by default',     body: 'Refuses to make up cycles for food it doesn\'t have data on. Would rather say "I don\'t know" than invent numbers.' },
    ]}
    personas={[
      { icon: UserCheck, title: 'Anyone stuck at 2am',  body: 'Machine started making a weird noise mid-cycle? Ask right now — no need to wait for a Facebook group to reply.' },
      { icon: Beaker,    title: 'Recipe developers',    body: 'Trying a new product for the first time? Get a starter cycle grounded in the closest published lesson, then iterate.' },
      { icon: Sparkles,  title: 'Curious learners',     body: 'Perfect way to poke at the science without reading 4 whitepapers — ask "why does secondary drying need to be so long?" and get the physics in a paragraph.' },
    ]}
    faqs={[
      { q: 'Which AI model powers the assistant?',
        a: 'A frontier-class model (GPT / Claude / Gemini switchable) wrapped with a retrieval layer that pulls from our lessons and cycle library first, so answers are grounded in freeze-dry reality — not the general internet.' },
      { q: 'Is my data private?',
        a: 'Yes. Conversations aren\'t used to train external models. Only you (and admins for support purposes) can see your chat history.' },
      { q: 'Can it read my saved batches?',
        a: 'If you\'re signed in and grant access, yes — you can say "look at my last batch of tomatoes and tell me why the yield was low" and it\'ll pull the batch record.' },
      { q: 'Does it work on my phone?',
        a: 'Yes — the app is a Progressive Web App (a website that installs like a real mobile app when you tap "Add to Home Screen"), so it works exactly like a native chat app right beside your machine, even without opening a browser.' },
    ]}
    related={[
      { slug: 'learning',    title: 'Learning & Courses', icon: BookOpen,   blurb: 'The knowledge base the assistant reads from.' },
      { slug: 'calculators', title: 'Calculators',        icon: Calculator, blurb: 'One-click hand-off from AI answer to real math.' },
      { slug: 'community',   title: 'Community Forum',    icon: Users,      blurb: 'For questions no assistant should be answering alone.' },
    ]}
    finalCtaTitle="Stop Googling ‘why is my freeze dryer beeping’."
    finalCtaBody="Sign up free — the AI Assistant is included on every tier."
  />
);

export default AIAssistantPage;
